import { useMemo } from 'react'
import type { EnhancedProductFamilyId, EnhancedProductGroup, EnhancedProductGroupId } from '#api/enhancements/enhancedTmsApiTypes'
import { fail } from '#app/assert'
import { useAppSelector } from '#app/store'
import { filterConfiguration, makeDefaultFilterOperators } from '#app/store/hooks/useFilteredProductGroups'
import type { TruckConfiguration } from '#app/store/slices/configurationSlice'
import type { ConfigurationEnrichmentData } from '#app/store/slices/enrichConfiguration'
import type { AppProductFamily, AppProductGroup, ProductPath } from '#app/types/AppTypes'
import type { FilterState } from '../Details/Filter/FilterState'
import type { ConfigurationDiff } from './useConfigurationDifference'

export type GroupDataEntry = {
  type: 'group'
  affected: boolean
  missing: boolean
  priceChanged: boolean
  id: ProductPath
} & ( // one of the two has to exist when there is a data entry
  | {
      base: AppProductGroup
      comparison?: AppProductGroup
    }
  | {
      base?: AppProductGroup
      comparison: AppProductGroup
    }
)

export type FamilyDataEntry = {
  type: 'family'
  affected: boolean
  missing: boolean
  priceChanged: boolean
  id: ProductPath
} & ( // similar spiel: either of the two has to exist
  | {
      base: AppProductFamily
      comparison?: AppProductFamily
    }
  | {
      base?: AppProductFamily
      comparison: AppProductFamily
    }
)

export type ComparisonDataEntry = GroupDataEntry | FamilyDataEntry

type GroupEntryWithSortIndices = { type: 'group'; id: EnhancedProductGroupId; sortIndices: number[]; isBase?: boolean }
type FamilyEntryWithSortIndices = { type: 'family'; id: EnhancedProductFamilyId; sortIndices: number[]; isBase?: boolean }
type TreeEntryWithSortIndices = GroupEntryWithSortIndices | FamilyEntryWithSortIndices

type MergeTreeContext = {
  entries: TreeEntryWithSortIndices[]
  groupEntryByGroupId: Map<EnhancedProductGroupId, GroupEntryWithSortIndices>
  familyEntryByFamilyId: Map<EnhancedProductFamilyId, FamilyEntryWithSortIndices>
}

const getAllSubGroupsRecursive = (groups: EnhancedProductGroup[], isBase: boolean, sortIndices: number[], context: MergeTreeContext) => {
  const { entries, groupEntryByGroupId, familyEntryByFamilyId } = context

  for (let i = 0; i < groups.length; i++) {
    const group = groups[i]
    let parent = groupEntryByGroupId.get(group.id)

    if (!parent) {
      parent = { type: 'group', id: group.id, sortIndices: [...sortIndices, i], isBase }
      entries.push(parent)
      groupEntryByGroupId.set(group.id, parent)
    }

    if (group.subGroups) {
      getAllSubGroupsRecursive(group.subGroups, isBase, parent.sortIndices, context)
    }

    if (group.families) {
      const familyOffset = group.subGroups?.length ?? 0
      for (let j = 0; j < group.families.length; j++) {
        const family = group.families[j]
        if (!familyEntryByFamilyId.has(family.id)) {
          const familyEntry = {
            type: 'family',
            id: family.id,
            sortIndices: [...parent.sortIndices, familyOffset + j],
            isBase,
          } satisfies FamilyEntryWithSortIndices
          entries.push(familyEntry)
          familyEntryByFamilyId.set(family.id, familyEntry)
        }
      }
    }
  }
}

const getSortedMergeTree = (
  baseConfiguration: { configuration: TruckConfiguration; enrichment: ConfigurationEnrichmentData },
  comparisonConfiguration: { configuration: TruckConfiguration; enrichment: ConfigurationEnrichmentData },
) => {
  const context: MergeTreeContext = { entries: [], groupEntryByGroupId: new Map(), familyEntryByFamilyId: new Map() }

  // first flatten all groups from the base configuration, store their sort indices by level
  getAllSubGroupsRecursive(baseConfiguration.enrichment.rootGroups, true, [], context)
  // then do the same for the comparison configuration, but only add sort indices if the group is already known
  getAllSubGroupsRecursive(comparisonConfiguration.enrichment.rootGroups, false, [], context)

  const sortedEntries = context.entries.sort((a, b) => {
    for (let i = 0; i < Math.min(a.sortIndices.length, b.sortIndices.length); i++) {
      if (a.sortIndices[i] !== b.sortIndices[i]) {
        return a.sortIndices[i] - b.sortIndices[i]
      }
    }
    // if all compared indices are equal, the shorter one (parent) comes first
    if (a.sortIndices.length !== b.sortIndices.length) {
      return a.sortIndices.length - b.sortIndices.length
    }
    // same position and depth: base entries before comparison entries
    if (a.isBase !== b.isBase) {
      return a.isBase ? -1 : 1
    }
    return 0
  })

  const topLevelGroups = sortedEntries
    .filter((e): e is GroupEntryWithSortIndices => e.type === 'group' && e.sortIndices.length === 1)
    .map((g) => (g.isBase ? baseConfiguration.enrichment.enrichedGroups[g.id] : comparisonConfiguration.enrichment.enrichedGroups[g.id]))
    .filter((x) => !!x)

  return { sortedEntries, topLevelGroups }
}

export const useLinearizeComparisonData = ({
  baseConfiguration,
  comparisonConfiguration,
  diff,
  filter = {},
}: {
  baseConfiguration: { configuration: TruckConfiguration; enrichment: ConfigurationEnrichmentData } | undefined
  comparisonConfiguration: { configuration: TruckConfiguration; enrichment: ConfigurationEnrichmentData } | undefined
  diff: ConfigurationDiff | undefined
  filter: FilterState | undefined
}) => {
  const hiddenGroups = useAppSelector((state) => state.truckConfigurations.meta.hiddenGroups)

  return useMemo(() => {
    if (!baseConfiguration || !comparisonConfiguration || !diff) {
      return {
        filteredLinearizedEntries: [],
        allLinearizedEntries: [],
      }
    }
    const linearizedDataEntries: ComparisonDataEntry[] = []
    // linearized group ids that only have to be looked up on both enrichments, then looked up if there's families in them
    const { sortedEntries } = getSortedMergeTree(baseConfiguration, comparisonConfiguration)

    const baseGroups = baseConfiguration.configuration.productGroups.map((x) => baseConfiguration.enrichment.enrichedGroups[x.id])
    const comparisonGroups = comparisonConfiguration.configuration.productGroups.map((x) => comparisonConfiguration.enrichment.enrichedGroups[x.id])

    const baseFilterData = filterConfiguration(
      baseGroups.filter((x) => !!x),
      makeDefaultFilterOperators({
        hiddenGroups,
        searchText: filter.search,
        hasInsights: filter.showInsights,
        hasEdits: filter.showEdited,
        hasFavorites: filter.showFavorites,
        hasPrintOptions: filter.showPrint,
        displayFamilies: filter.displayFamilies,
        includeOnlySelectedVariants: true,
        displayGroupsWithFamilies: filter.displayGroup,
      }),
      'OR',
    )

    const comparisonFilterData = filterConfiguration(
      comparisonGroups.filter((x) => !!x),
      makeDefaultFilterOperators({
        hiddenGroups,
        searchText: filter.search,
        hasInsights: filter.showInsights,
        hasEdits: filter.showEdited,
        hasFavorites: filter.showFavorites,
        hasPrintOptions: filter.showPrint,
        displayFamilies: filter.displayFamilies,
        includeOnlySelectedVariants: true,
        displayGroupsWithFamilies: filter.displayGroup,
      }),
      'OR',
    )

    const groupPathsWithFamilies = new Set()

    for (const entry of sortedEntries) {
      if (entry.type === 'group') {
        const groupId = entry.id

        const baseGroup = baseConfiguration.enrichment.enrichedGroups[groupId]
        const comparisonGroup = comparisonConfiguration?.enrichment.enrichedGroups[groupId]
        const affected = diff.affectedGroups.has(groupId)
        const missing = diff.missingGroups.has(groupId)
        const priceChanged = diff.priceChangeGroups.has(groupId)

        // either the base exists
        if (baseGroup) {
          linearizedDataEntries.push({
            type: 'group',
            base: baseGroup,
            comparison: comparisonGroup,
            affected,
            missing,
            priceChanged,
            id: baseGroup.path,
          })
        }
        // or the comparison
        else if (comparisonGroup) {
          linearizedDataEntries.push({
            type: 'group',
            base: baseGroup,
            comparison: comparisonGroup,
            affected,
            missing,
            priceChanged,
            id: comparisonGroup.path,
          })
        }
        // but not neither
        else {
          fail('Neither baseGroup or comparisonGroup exists')
        }
      } else if (entry.type === 'family') {
        const familyId = entry.id

        const baseFamily = baseConfiguration.enrichment.enrichedFamilies[familyId]
        const comparisonFamily = comparisonConfiguration.enrichment.enrichedFamilies[familyId]

        const baseGroup = baseFamily ? baseConfiguration.enrichment.enrichedGroups[baseFamily.parentGroup.id] : undefined
        const comparisonGroup = comparisonFamily ? comparisonConfiguration?.enrichment.enrichedGroups[comparisonFamily.parentGroup.id] : undefined
        const parentGroup = baseGroup ?? comparisonGroup

        const affected = diff.affectedFamilies.has(familyId)
        const missing = diff.missingFamilies.has(familyId)
        const priceChanged = diff.priceChangeFamilies.has(familyId)

        // either this exists
        if (baseFamily) {
          if (parentGroup) {
            groupPathsWithFamilies.add(parentGroup.path)
            if (parentGroup.parentGroup) {
              groupPathsWithFamilies.add(parentGroup.parentGroup.path)
            }
          }
          linearizedDataEntries.push({
            type: 'family',
            id: baseFamily.path,
            base: baseFamily,
            comparison: comparisonFamily,
            affected,
            missing,
            priceChanged,
          })
        }
        // or that one has to exist
        else if (comparisonFamily) {
          if (parentGroup) {
            groupPathsWithFamilies.add(parentGroup.path)
            if (parentGroup.parentGroup) {
              groupPathsWithFamilies.add(parentGroup.parentGroup.path)
            }
          }
          linearizedDataEntries.push({
            type: 'family',
            id: comparisonFamily.path,
            base: baseFamily,
            comparison: comparisonFamily,
            affected,
            missing,
            priceChanged,
          })
        }
        // but not neither
        if (!baseFamily && !comparisonFamily) {
          fail('Neither baseFamily or comparisonFamily exists')
        }
      }
    }

    function filterEntries(entry: ComparisonDataEntry) {
      const isFiltered = baseFilterData[entry.id] === 'filtered' && comparisonFilterData[entry.id] === 'filtered'
      const isEmptyGroup = entry.type === 'group' && !groupPathsWithFamilies.has(entry.id)
      return !isFiltered && !isEmptyGroup
    }
    const filteredLinearizedEntries = linearizedDataEntries.filter(filterEntries)

    return { filteredLinearizedEntries, allLinearizedEntries: linearizedDataEntries }
  }, [baseConfiguration, comparisonConfiguration, diff, hiddenGroups, filter])
}
